import { queryOptions } from "@tanstack/react-query";
import { DynamicProps } from "../types";
import { parseAsset } from "../utils";
import { QueryKeys } from "@/modules/core";
import { callRPC } from "@/modules/core/hive-tx";

// This query powers wallet/HP/reward math that should stay close to chain state.
// Keep a short refresh cadence despite the 5 RPC calls.
const DYNAMIC_PROPS_REFRESH_MS = 60 * 1000;

export function getDynamicPropsQueryOptions() {
  return queryOptions({
    queryKey: QueryKeys.core.dynamicProps(),
    refetchInterval: DYNAMIC_PROPS_REFRESH_MS,
    staleTime: DYNAMIC_PROPS_REFRESH_MS,
    queryFn: async ({ signal }): Promise<DynamicProps> => {
      // Get raw blockchain data — all five calls are independent, run in parallel.
      // Hardfork properties is wrapped with catch since not all nodes support it.
      const [rawGlobalDynamic, rawFeedHistory, rawChainProps, rawRewardFund, rawHardforkProps] = await Promise.all([
        callRPC("condenser_api.get_dynamic_global_properties", [], undefined, undefined, signal),
        callRPC("condenser_api.get_feed_history", [], undefined, undefined, signal),
        callRPC("condenser_api.get_chain_properties", [], undefined, undefined, signal),
        callRPC("condenser_api.get_reward_fund", ["post"], undefined, undefined, signal),
        callRPC("database_api.get_hardfork_properties", {}, undefined, undefined, signal)
          .catch(() => ({ current_hardfork_version: "1.28.0", last_hardfork: 28 })),
      ]) as [any, any, any, any, any];

      // Calculate derived values for backward compatibility
      // parseAsset handles both string format ("200905388484 HIVE") and NAI format ({ amount, nai, precision })
      const totalVestingSharesAmount = parseAsset(rawGlobalDynamic.total_vesting_shares).amount;
      const totalVestingFundAmount = parseAsset(rawGlobalDynamic.total_vesting_fund_hive).amount;

      // Guard against division by zero/NaN/Infinity
      let hivePerMVests = 0;
      if (
        Number.isFinite(totalVestingSharesAmount) &&
        totalVestingSharesAmount !== 0 &&
        Number.isFinite(totalVestingFundAmount)
      ) {
        hivePerMVests = (totalVestingFundAmount / totalVestingSharesAmount) * 1e6;
      }
      const base = parseAsset(rawFeedHistory.current_median_history.base).amount;
      const quote = parseAsset(rawFeedHistory.current_median_history.quote).amount;
      const fundRecentClaims = parseFloat(rawRewardFund.recent_claims);
      const fundRewardBalance = parseAsset(rawRewardFund.reward_balance).amount;
      const votePowerReserveRate = Number(rawGlobalDynamic.vote_power_reserve_rate ?? 0);
      const authorRewardCurve = rawRewardFund.author_reward_curve ?? "linear";
      const contentConstant = Number(rawRewardFund.content_constant ?? 0);
      const currentHardforkVersion = String(rawHardforkProps.current_hardfork_version ?? "0.0.0");
      const lastHardfork = Number(rawHardforkProps.last_hardfork ?? 0);
      const hbdPrintRate = rawGlobalDynamic.hbd_print_rate;
      const hbdInterestRate = rawGlobalDynamic.hbd_interest_rate;
      const headBlock = rawGlobalDynamic.head_block_number;
      const totalVestingFund = totalVestingFundAmount;
      const totalVestingShares = totalVestingSharesAmount;
      const virtualSupply = parseAsset(rawGlobalDynamic.virtual_supply).amount;
      const vestingRewardPercent = rawGlobalDynamic.vesting_reward_percent || 0;
      const accountCreationFee = rawChainProps.account_creation_fee;

      return {
        // Backward compatible transformed fields (camelCase, parsed)
        hivePerMVests,
        base,
        quote,
        fundRecentClaims,
        fundRewardBalance,
        votePowerReserveRate,
        authorRewardCurve,
        contentConstant,
        currentHardforkVersion,
        lastHardfork,
        hbdPrintRate,
        hbdInterestRate,
        headBlock,
        totalVestingFund,
        totalVestingShares,
        virtualSupply,
        vestingRewardPercent,
        accountCreationFee,

        // Raw blockchain data (snake_case, unparsed) for direct use
        // Includes ALL fields from the blockchain responses
        raw: {
          globalDynamic: rawGlobalDynamic,
          feedHistory: rawFeedHistory,
          chainProps: rawChainProps,
          rewardFund: rawRewardFund,
          hardforkProps: rawHardforkProps,
        },
      } as DynamicProps;
    },
  });
}
